import { FC, useState } from "react";
import moment from "moment";
import Modal from "../Modal";
import classNames from "classnames";
import { useNavigate } from "react-router-dom";
import {
  getPaymentsDetails,
  loaderService,
  previewFileDO,
} from "../../services";
import { LinkText, PrimaryButton, SecondaryButton } from "../Buttons";
import { bankAccountList } from "../../interfaces/Dtos/BankAccountDTO";
import {
  ExclamationTriangleIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import {
  currencyExchange,
  currencyExchangeText,
  formatPaymentStatus,
  useCurrencyExchanges,
} from "../../utils";
import {
  BankAccountInterface,
  DocumentInterface,
  PaymentDetailsDto,
  PaymentInterface,
  PaymentMethodEnum,
  PaymentStatusEnum,
} from "../../interfaces";
import { useAppSelector } from "../../store/hooks";
import WarningModal from "../WarningModal";
import { FormSelect, FormTextInput } from "../FormFields";

interface PaymentItemProps {
  index: number;
  transaction: PaymentInterface;
  document?: DocumentInterface;
  VTIDs?: string[];
  last?: boolean;
  onDeletePayment: () => void;
  onAddProof: () => void;
  onChangeProof: () => void;
  onDeleteProof: () => void;
  onApproveReintegration?: (
    reintegration: PaymentInterface,
    bankAccount: BankAccountInterface,
    transactionDetails?: PaymentDetailsDto
  ) => void;
  onCancelPayment: (payment: PaymentInterface) => void;
}
const PaymentItem: FC<PaymentItemProps> = ({
  index,
  transaction,
  document,
  VTIDs,
  last,
  onDeletePayment,
  onAddProof,
  onChangeProof,
  onDeleteProof,
  onCancelPayment,
  onApproveReintegration = () => {},
}) => {
  const navigate = useNavigate();
  const loading = loaderService.useIsLoading();
  const user = useAppSelector((state) => state.user);
  const exchanges = useCurrencyExchanges(transaction.creationDate);

  const [openWarningModal, setOpenWarningModal] = useState(false);
  const [paymentToCancel, setPaymentToCancel] = useState<PaymentInterface>();
  const [bankAccount, setBankAccount] = useState<BankAccountInterface>();
  const [approveReintegrationModal, setApproveReintegrationModal] =
    useState(false);
  const [cancelReintegrationModal, setCancelReintegrationModal] =
    useState(false);
  const [approveInstantCreditModal, setApproveInstantCreditModal] =
    useState(false);
  const [transactionDetails, setTransactionDetails] =
    useState<PaymentDetailsDto>();
  const [openRefundWarningModal, setOpenRefundWarningModal] = useState(false);
  const [zelleReference, setZelleReference] = useState<string>("");

  const refundWarningAmount = 500;

  const paymentMethodName = (transaction: PaymentInterface): string => {
    const methodName = transaction.paymentMethod.paymentMethodName;
    if (
      transaction.paymentMethod.paymentMethodID ===
        PaymentMethodEnum.EFECTIVO ||
      transaction.paymentMethod.paymentMethodID ===
        PaymentMethodEnum.VUELTO_EFECTIVO
    ) {
      if (transaction.paymentMethod.currencyID === 1) {
        return `${methodName} en Bolívares`;
      }
      return `${methodName} en Dólares`;
    }

    return methodName;
  };

  return (
    <tr
      className={classNames(
        index % 2 === 0 && "bg-gray-50",
        last && "border-b-2 border-gray-300"
      )}
    >
      <td className="text-xs px-4 py-2 font-bold truncate">
        <div className="flex items-center">
          <div hidden={user.user?.roleName === "Consulta Innovus"}>
            {transaction.status !== PaymentStatusEnum.CANCELED &&
              transaction.status !== PaymentStatusEnum.REJECT &&
              transaction.status !== PaymentStatusEnum.APPROVE &&
              transaction.paymentMethod.paymentMethodID !==
                PaymentMethodEnum.ZELLE && // Zelle payments deletion to be defined later
              (transaction.paymentMethod.paymentMethodID !==
                PaymentMethodEnum.REINTEGRO ||
                user?.user!.roleName === "Superadministrador") && (
                <button
                  type="button"
                  onClick={onDeletePayment}
                  className="inline-flex p-2 rounded-full bg-red-50 hover:bg-red-100 cursor-pointer mr-2"
                  aria-label="Eliminar pago"
                >
                  <TrashIcon className="h-4 w-4 text-red-500" />
                </button>
              )}
          </div>

          <div>
            <p>{paymentMethodName(transaction)}</p>
            {!!transaction.creationDate && (
              <p className="text-gray-700">
                <span className="text-gray-800 font-normal">Registrado: </span>{" "}
                {new Date(transaction.creationDate)
                  .toLocaleDateString("es-VE", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                  .replace("a", "AM")
                  .replace("p", "PM")
                  .slice(0, -4)}
              </p>
            )}
          </div>
        </div>
      </td>

      <td className="text-xs px-4 py-2 font-bold text-gray-500 truncate">
        {(transaction.isRetention ||
          [
            PaymentMethodEnum.ZELLE,
            PaymentMethodEnum.PAGO_MOVIL_P2C,
            PaymentMethodEnum.TRANSFERENCIA_BANCARIA_INMEDIATA,
            PaymentMethodEnum.PAGO_MERCHANT_TARJETA_CONCILIACION_MANUAL,
          ].includes(transaction.paymentMethod.paymentMethodID)) &&
          transaction.proofOfPayment && (
            <div>
              <div className="flex gap-2">
                <p className="font-semibold text-gray-500 truncate">
                  <LinkText
                    text="Ver comprobante"
                    onClick={() => {
                      previewFileDO(transaction.proofOfPayment!);
                    }}
                  />
                </p>
                <p
                  className={classNames(
                    "font-semibold text-gray-500 truncate",
                    (transaction.status === PaymentStatusEnum.CANCELED ||
                      transaction.status === PaymentStatusEnum.REJECT ||
                      (transaction.status === PaymentStatusEnum.APPROVE &&
                        !transaction.isRetention)) &&
                      "hidden"
                  )}
                >
                  <LinkText text="Cambiar" onClick={onChangeProof} />
                </p>
              </div>
              <p className="hidden">
                {moment(transaction.paymentDate).format("YYYY-MM-DD hh:mm a")}
              </p>
            </div>
          )}

        {(transaction.isRetention ||
          [
            PaymentMethodEnum.ZELLE,
            PaymentMethodEnum.PAGO_MOVIL_P2C,
            PaymentMethodEnum.TRANSFERENCIA_BANCARIA_INMEDIATA,
            PaymentMethodEnum.PAGO_MERCHANT_TARJETA_CONCILIACION_MANUAL,
          ].includes(transaction.paymentMethod.paymentMethodID)) &&
          !transaction.proofOfPayment &&
          user.user?.roleName !== "Consulta Innovus" &&
          transaction.status === PaymentStatusEnum.PENDING && (
            <LinkText text="Agregar comprobante" onClick={onAddProof} />
          )}

        {transaction.paymentMethod.paymentMethodName === "Nota de Crédito" &&
          document?.documentAffectedID && (
            <LinkText
              text="Ver comprobante"
              onClick={() =>
                navigate(`/documents/${document?.documentAffectedID}`)
              }
            />
          )}
      </td>

      <td className="text-xs px-4 py-2 font-semibold font-bold text-gray-500 truncate">
        {transaction.reference?.includes(":")
          ? transaction.reference.split(":")[0]
          : transaction.reference}
      </td>

      <td className="text-xs px-4 py-2 font-semibold font-bold text-gray-500 truncate">
        {[
          PaymentMethodEnum.CREDITO_INMEDIATO_CM,
          PaymentMethodEnum.REINTEGRO,
        ].includes(transaction.paymentMethod.paymentMethodID)
          ? transaction.status === PaymentStatusEnum.PENDING
            ? formatPaymentStatus(transaction.status ?? 1)
            : transaction.destBankID === 0
            ? "Reintegro no procesado"
            : formatPaymentStatus(transaction.status ?? 1)
          : formatPaymentStatus(transaction.status ?? 1)}
        <br />
        {[
          PaymentMethodEnum.REINTEGRO,
          PaymentMethodEnum.REINTEGRO_ZELLE,
          PaymentMethodEnum.CREDITO_INMEDIATO_CM,
        ].includes(transaction.paymentMethod.paymentMethodID) &&
          transaction.status === PaymentStatusEnum.PENDING &&
          user.user?.roleName === "Superadministrador" && (
            <>
              <div className="mt-2 mb-2 items-start align-middle">
                {transaction.paymentMethod.paymentMethodID ===
                  PaymentMethodEnum.REINTEGRO_ZELLE && (
                  <FormTextInput
                    className="mb-2"
                    placeholder="Ingrese la referencia del reintegro Zelle"
                    name="zelleReference"
                    value={zelleReference}
                    onChange={(e) => setZelleReference(e.target.value)}
                  />
                )}
                <LinkText
                  text="Aprobar reintegro"
                  disabled={
                    transaction.paymentMethod.paymentMethodID ===
                      PaymentMethodEnum.REINTEGRO && bankAccount === undefined ||
                    (transaction.paymentMethod.paymentMethodID ===
                      PaymentMethodEnum.REINTEGRO_ZELLE &&
                      zelleReference === "")
                  }
                  className="text-xs mb-1 mr-3"
                  onClick={async () => {
                    if (
                      -currencyExchange(transaction.amount, exchanges, "USD") >
                      refundWarningAmount
                    ) {
                      setOpenRefundWarningModal(true);
                    } else {
                      transaction.paymentMethod.paymentMethodID ===
                      PaymentMethodEnum.CREDITO_INMEDIATO_CM
                        ? setApproveInstantCreditModal(true)
                        : setApproveReintegrationModal(true);
                      const paymentsDetails = await getPaymentsDetails({
                        paymentID: transaction.paymentID,
                      });
                      setTransactionDetails(paymentsDetails.model?.[0]);
                    }

                  }}
                />
                {transaction.paymentMethod.paymentMethodID ===
                  PaymentMethodEnum.CREDITO_INMEDIATO_CM && (
                  <LinkText
                    text="Reintegro no procesado"
                    className="text-xs mb-1"
                    onClick={() => setCancelReintegrationModal(true)}
                  />
                )}
                {transaction.paymentMethod.paymentMethodID ===
                  PaymentMethodEnum.REINTEGRO && (
                  <FormSelect
                    label="Cuenta pagadora"
                    name="bank"
                    options={bankAccountList}
                    optionString={(method) =>
                      `${method.bankName ?? "Buscar..."}`
                    }
                    selected={bankAccount}
                    onSelectOption={(bank) => setBankAccount(bank)}
                  />
                )}
              </div>

              {/* Warning modal max refund amount  */}
              <WarningModal
                openModal={openRefundWarningModal}
                setOpenModal={setOpenRefundWarningModal}
                message={`Consultar con Tesorería antes de procesar reintegros mayores a ${currencyExchangeText(
                  refundWarningAmount,
                  exchanges,
                  "BS",
                  "USD"
                )} (${refundWarningAmount}$). ¿Desea continuar?`}
                onPrimaryButton={async () => {
                  transaction.paymentMethod.paymentMethodID ===
                  PaymentMethodEnum.CREDITO_INMEDIATO_CM
                    ? setApproveInstantCreditModal(true)
                    : setApproveReintegrationModal(true);
                  const paymentsDetails = await getPaymentsDetails({
                    paymentID: transaction.paymentID,
                  });
                  setTransactionDetails(paymentsDetails.model?.[0]);
                  setOpenRefundWarningModal(false);
                }}
                showSecondaryButton={true}
                onSecondaryButton={() => setOpenRefundWarningModal(false)}
              />

              {/* Warning modal refund annulation */}
              <WarningModal
                openModal={cancelReintegrationModal}
                setOpenModal={setCancelReintegrationModal}
                message="¿Está seguro que desea confirmar el reintegro como reintegro no procesado?"
                onPrimaryButton={() => {
                  setCancelReintegrationModal(false);
                  const newTransaction = { ...transaction };
                  newTransaction.paymentMethod = {
                    paymentMethodID: PaymentMethodEnum.REINTEGRO_NO_PROCESADO,
                    paymentMethodName: "Reintegro no Procesado",
                    currencyID: transaction.paymentMethod.currencyID,
                  };
                  newTransaction.status = PaymentStatusEnum.APPROVE;
                  onApproveReintegration(newTransaction, bankAccount!);
                  setBankAccount({} as BankAccountInterface);
                }}
                showSecondaryButton={true}
                onSecondaryButton={() => setCancelReintegrationModal(false)}
              />

              {/* Warning modal refund approval deprecated */}
              <WarningModal
                openModal={approveReintegrationModal}
                setOpenModal={setApproveReintegrationModal}
                message="¿Está seguro que desea aprobar el reintegro?"
                onPrimaryButton={() => {
                  setApproveReintegrationModal(false);
                  if (
                    transaction.paymentMethod.paymentMethodID ===
                    PaymentMethodEnum.REINTEGRO_ZELLE
                  ) {
                    const zelleBankAccount: BankAccountInterface = {
                      bankID: 32,
                    }; // Bank ID for Zelle
                    const zelleTransaction = {
                      ...transaction,
                      reference: zelleReference,
                    };
                    onApproveReintegration(
                      zelleTransaction,
                      zelleBankAccount
                    );
                  } else {
                    onApproveReintegration(transaction, bankAccount!);
                  }
                  setBankAccount({} as BankAccountInterface);
                }}
                showSecondaryButton={true}
                onSecondaryButton={() => setApproveReintegrationModal(false)}
              />

              {/* Warning modal IC CM */}
              <Modal
                openModal={approveInstantCreditModal}
                setOpenModal={setApproveInstantCreditModal}
              >
                <div
                  className="flex flex-col items-center justify-center"
                  style={{ maxWidth: "20rem" }}
                >
                  <p className="my-2 text-lg text-center text-gray-700">
                    Se procesará el Crédito Inmediato con los siguientes datos:
                  </p>

                  <div className="max-w-lg text-start rounded-lg shadow-lg w-fit p-6 border">
                    <p className="text-md text-gray-500 font-bold mb-2">
                      DATOS DE PAGO
                    </p>
                    <p className=" text-md text-gray-500 mb-2">
                      Documento de identidad:{" "}
                      <span className="text-gray-900 font-bold">
                        {transactionDetails?.identificationType?.replace(
                          /-$/,
                          ""
                        )}
                        {transactionDetails?.identificationNumber}
                      </span>
                    </p>
                    <p className=" text-md text-gray-500 mb-2">
                      Teléfono:{" "}
                      <span className="text-gray-900 font-bold">
                        {transactionDetails?.phoneNumber}
                      </span>
                    </p>
                    <p className=" text-md text-gray-500 mb-2">
                      Banco:{" "}
                      <span className="text-gray-900 font-bold">
                        {transactionDetails?.destinationBankName}
                      </span>
                    </p>
                    <p className=" text-md text-gray-500 mb-2">
                      Monto:{" "}
                      <span className="text-gray-900 font-bold">
                        {-1 * (transactionDetails?.amount ?? 0)} Bs
                      </span>
                    </p>
                  </div>

                  <div className="mt-4 flex flex-row justify-center gap-12">
                    <SecondaryButton
                      className="px-4"
                      onClick={() => setApproveInstantCreditModal(false)}
                    >
                      Cancelar
                    </SecondaryButton>

                    <PrimaryButton
                      className="px-4"
                      disabled={loading}
                      onClick={() => {
                        setApproveInstantCreditModal(false);
                        onApproveReintegration(
                          transaction,
                          bankAccount!,
                          transactionDetails
                        );
                        setBankAccount({} as BankAccountInterface);
                      }}
                    >
                      Aceptar
                    </PrimaryButton>
                  </div>
                </div>
              </Modal>
            </>
          )}
        {[
          PaymentMethodEnum.PAGO_MERCHANT_TARJETAS_DE_CREDITO,
          PaymentMethodEnum.PAGO_MERCHANT_TARJETA_DE_DEBITO,
          PaymentMethodEnum.PAGO_MERCHANT_TARJETA_CONCILIACION_MANUAL,
        ].includes(transaction.paymentMethod.paymentMethodID) &&
          transaction.status === PaymentStatusEnum.PENDING &&
          !!VTIDs &&
          VTIDs.includes(
            transaction.reference?.split(":")[1]?.toLowerCase() ?? ""
          ) && (
            <>
              <LinkText
                text="Anular pago"
                className="text-xs"
                onClick={() => {
                  setPaymentToCancel(transaction);
                  setOpenWarningModal(true);
                }}
              />

              {/* Warning modal */}
              <Modal
                openModal={openWarningModal}
                setOpenModal={setOpenWarningModal}
              >
                <div
                  className="flex flex-col items-center justify-center"
                  style={{ maxWidth: "20rem" }}
                >
                  <div className="flex flex-col items-center justify-center w-full">
                    <ExclamationTriangleIcon className="w-32 h-32" />
                  </div>
                  <p className="mt-2 text-lg text-center text-gray-700">
                    ¿Está seguro que desea anular el pago?
                  </p>
                  <div className="mt-4 flex flex-row justify-center gap-12">
                    <SecondaryButton
                      className="px-4"
                      onClick={() => setOpenWarningModal(false)}
                    >
                      Cancelar
                    </SecondaryButton>

                    <PrimaryButton
                      className="px-4"
                      disabled={loading}
                      onClick={() => {
                        setOpenWarningModal(false);
                        onCancelPayment(paymentToCancel!);
                      }}
                    >
                      Aceptar
                    </PrimaryButton>
                  </div>
                </div>
              </Modal>
            </>
          )}
      </td>

      <td className="text-xs text-right px-4 py-2 font-semibold w-auto text-gray-600 truncate">
        <div className="flex flex-col items">
          <p className="font-semibold text-gray-600 truncate">
            {currencyExchangeText(transaction.amount, exchanges, "USD")}
          </p>
          <p className="text-xs text-gray-400 ">
            {currencyExchangeText(transaction.amount, exchanges, "BS")}
          </p>
        </div>
      </td>
    </tr>
  );
};

export default PaymentItem;
